import { useHqAndDomains } from "api/services";
import { userSelectedHq } from "atoms/auth";
import { HqIdRouteParams } from "components/SelectHqOfficeCombobox/helper";
import useUserInfo from "hooks/useUserInfo";
import React, { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useRecoilState } from "recoil";

interface Props {
  children: React.ReactNode;
}

export const HqPathUpdater = ({ children }: Props) => {
  const [selectedHq, setSelectedHq] = useRecoilState(userSelectedHq);
  const selectedHqId = selectedHq?.id;
  const { hqId } = useParams<HqIdRouteParams>();
  const navigate = useNavigate();
  const { userEmail } = useUserInfo();
  const { data: hqs } = useHqAndDomains(userEmail);

  // This effect is responsible for navigating to the correct route when the user selects a new HQ from the sidebar dropdown.
  // An alternative solution would be to only do this route change when the user interacts with the dropdown but
  // that might have side effects in routes where we haven't yet verified this behavior.
  useEffect(() => {
    if (!hqId || hqId === selectedHqId) {
      return;
    }

    if (selectedHqId) {
      const root = location.pathname.startsWith("/customer")
        ? "customer"
        : "back-office";

      // This assumes that the path is /customer/hq/:id/ then it will extract the path after that.
      const currentRoute = location.pathname.split("/").slice(4).join("/");
      navigate(`/${root}/hq/${selectedHqId}/${currentRoute}`);
    }
  }, [selectedHqId, navigate, hqId]);

  // This effect is responsible for updating the selected hq when the user navigates to a new route with a different hqId.
  // We cannot put the route param hq id in this effect because then we get an infinite loop between the two effects.
  useEffect(() => {
    if (!hqId) {
      return;
    }
    const hq = hqs?.find(hq => hq.id === hqId);

    if (hq) {
      setSelectedHq(hq);
    }
  }, [hqId, hqs, setSelectedHq]);

  if (!selectedHq && !hqId) return null;
  return <>{children}</>;
};
